import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FiX } from "react-icons/fi";

import subscriptionPaymentService from "../../api/subscriptionPaymentService";
import { refreshUserProfile } from "../../redux/slices/authSlice";
import usePlan from "../../hooks/usePlan";
// Reuse the upgrade-pro-modal styling — same layout, colours, sizes
// and mobile bottom-sheet behaviour, only the icon / copy / handlers
// change.
import "./ComparisonModal.scss";

const SWIPE_CLOSE_THRESHOLD = 110;
const SWIPE_MOBILE_BREAKPOINT = 576;

/* Crown shape — matches the design's "premium plan" glyph. The warning
   badge that sits over the bottom-right corner is rendered separately
   below (so it can keep its own colour against the lighter tile bg). */
const CrownIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M3 19h18" />
    <path d="M5 17 3 7l5 4 4-7 4 7 5-4-2 10z" />
  </svg>
);

/* Lowercase 'i' in a circle — small "what's changing" info marker. */
const InfoIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

// One-time full-screen takeover shown to grandfathered Plus users (plan_id IN
// 1, 2) on their next app launch after the migration deploys. Any close path
// marks plus_migration_seen_at and the modal never returns. Three actions:
//   - Upgrade to Pro          → handleUpgrade
//   - Cancel subscription     → handleDowngrade
//   - Maybe later / dismiss   → handleKeepPlus
const PlusMigrationTakeover = () => {
  const user = useSelector((state) => state.auth?.user);
  const { isGrandfatheredPlus } = usePlan();
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* Mobile-only swipe-down-to-close (same gesture as CompareDailyLimitModal). */
  const dialogRef = useRef(null);
  const startYRef = useRef(null);
  const draggingRef = useRef(false);
  const [dragY, setDragY] = useState(0);

  const isVisible = Boolean(
    user && isGrandfatheredPlus && !user.plus_migration_seen_at,
  );

  const isMobile = () =>
    typeof window !== "undefined" &&
    window.innerWidth <= SWIPE_MOBILE_BREAKPOINT;

  const onTouchStart = (e) => {
    if (!isMobile()) return;
    startYRef.current = e.touches[0].clientY;
    draggingRef.current = true;
  };
  const onTouchMove = (e) => {
    if (!draggingRef.current || startYRef.current == null) return;
    const dy = e.touches[0].clientY - startYRef.current;
    setDragY(dy > 0 ? dy : 0);
  };
  const onTouchEnd = () => {
    if (!draggingRef.current) return;
    const finalDy = dragY;
    draggingRef.current = false;
    startYRef.current = null;
    if (finalDy > SWIPE_CLOSE_THRESHOLD) {
      setDragY(0);
      handleKeepPlus();
    } else {
      setDragY(0);
    }
  };

  useEffect(() => {
    if (!isVisible) setDragY(0);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const content = document.querySelector(".upgrade-pro-modal .modal-content");
    if (!content) return;
    if (dragY > 0) {
      content.style.transform = `translateY(${dragY}px)`;
      content.style.transition = "none";
    } else {
      content.style.transform = "";
      content.style.transition = "";
    }
  }, [dragY, isVisible]);

  if (!isVisible) return null;

  const dismissAndRefreshUser = async () => {
    try {
      await subscriptionPaymentService.dismissPlusMigration();
      // refreshUserProfile updates state.auth.user → this modal unmounts on
      // the next render because user.plus_migration_seen_at is now populated.
      await dispatch(refreshUserProfile());
    } catch (err) {
      console.error("dismiss-plus-migration failed:", err);
    }
  };

  // "Maybe later" — dismiss without changing the subscription.
  // Equivalent to the previous "Keep my Plus plan" action.
  const handleKeepPlus = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    await dismissAndRefreshUser();
    setIsProcessing(false);
  };

  const handleUpgrade = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    await dismissAndRefreshUser();
    setIsProcessing(false);
    navigate("/profile/subscription", { state: { defaultTab: "Pro" } });
  };

  const handleDowngrade = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      // End-of-billing-period cancellation so the user keeps the access
      // they've already paid for. Stripe's customer.subscription.deleted
      // webhook will move them to Free when the period ends.
      await subscriptionPaymentService.cancelSubscription({
        userId: user.id,
        cancellation_reason: "downgrade_from_plus_migration",
        cancellation_feedback: "",
        immediate: false,
      });
    } catch (err) {
      console.error("plus → free downgrade failed:", err);
    }
    await dismissAndRefreshUser();
    setIsProcessing(false);
  };

  // "Need help deciding?" — dismiss the takeover and drop the user on
  // the subscription page so they can compare plans / reach support.
  const handleCompareOrHelp = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    await dismissAndRefreshUser();
    setIsProcessing(false);
    navigate("/profile/subscription");
  };

  return (
    <Modal
      show={isVisible}
      backdrop="static"
      keyboard={false}
      centered
      dialogClassName="upgrade-pro-modal"
      backdropClassName="upgrade-pro-modal-backdrop"
    >
      <Modal.Body
        ref={dialogRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div
          className="upm-handle"
          aria-hidden="true"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        />

        <button
          type="button"
          className="upm-close"
          onClick={handleKeepPlus}
          aria-label="Close"
          disabled={isProcessing}
        >
          <FiX size={20} />
        </button>

        <div className="upm-icon-row">
          <div className="upm-icon" style={{ position: "relative" }}>
            <CrownIcon className="upm-icon-svg" />
            {/* Warning badge — bottom-right overlay with white ring so it
                pops off the lighter brand-50 tile background. */}
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                right: -4,
                bottom: -4,
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "var(--upm-brand-600, #027bb0)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                border: "2px solid #fff",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.2)",
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="8" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </span>
          </div>
        </div>

        <h2 className="upm-title">Plus plan is no longer available</h2>
        <p className="upm-sub">
          We&apos;ve simplified our plans to give you more value. Upgrade to Pro
          to continue enjoying premium features or cancel your current
          subscription.
        </p>

        <div
          className="upm-info"
          style={{
            alignItems: "flex-start",
            justifyContent: "flex-start",
            gap: 10,
            textAlign: "left",
          }}
        >
          <InfoIcon
            className="upm-info-spark"
            style={{ marginTop: 2, flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              className="upm-info-text"
              style={{
                textAlign: "left",
                fontWeight: 700,
                marginBottom: 4,
              }}
            >
              What&apos;s changing?
            </p>
            <p
              className="upm-info-text"
              style={{
                textAlign: "left",
                fontWeight: 400,
              }}
            >
              The Plus plan has been discontinued. Your current subscription
              will continue until the end of your billing cycle.
            </p>
          </div>
        </div>

        <div className="upm-ctas">
          <button
            type="button"
            className="upm-btn-primary"
            onClick={handleUpgrade}
            disabled={isProcessing}
          >
            <span className="upm-btn-primary-title">Upgrade to Pro</span>
            <span className="upm-btn-primary-sub">
              Get more features and the best experience
            </span>
          </button>

          <button
            type="button"
            className="upm-btn-secondary"
            onClick={handleDowngrade}
            disabled={isProcessing}
          >
            Cancel subscription
          </button>

          <button
            type="button"
            className="upm-newsletter"
            onClick={handleCompareOrHelp}
            disabled={isProcessing}
          >
            <span className="upm-newsletter-q">Continue with current plan</span>
            {/* <span className="upm-newsletter-cta">
              Compare plans or contact support
            </span> */}
          </button>
        </div>

        {/* <button
          type="button"
          className="upm-later"
          onClick={handleKeepPlus}
          disabled={isProcessing}
        >
          Maybe later
        </button> */}
      </Modal.Body>
    </Modal>
  );
};

export default PlusMigrationTakeover;
